/** Tutarlar decimal string ("1250.50"); toplamlar ve vergi tahminleri sunucuda hesaplanır, istemci yalnız gösterir. */
export type Money = string

export const ACCOUNT_TYPES = ['cash', 'bank', 'pos', 'credit_card', 'deposit'] as const
export type AccountType = (typeof ACCOUNT_TYPES)[number]
/** Kasa ve POS sayfası nakit/POS hesaplarını, Kartlar sayfası kredi kartı ve banka hesaplarını listeler. */
export type AccountScope = 'money' | 'cards'
export const SCOPE_ACCOUNT_TYPES: Record<AccountScope, readonly AccountType[]> = { money: ['cash', 'pos'], cards: ['credit_card', 'bank', 'deposit'] }

export const TX_KINDS = ['income', 'expense'] as const
export type TxKind = (typeof TX_KINDS)[number]

export const VAT_RATES = [0, 1, 10, 20] as const
export const WITHHOLDING_RATES = [0, 10, 15, 20] as const

export const PAYMENT_CHANNELS = ['transfer', 'card', 'cash', 'pos'] as const
export type PaymentChannel = (typeof PAYMENT_CHANNELS)[number]

export const REVIEW_STATUSES = ['pending', 'reviewing', 'approved'] as const
export type ReviewStatus = (typeof REVIEW_STATUSES)[number]

export const VAT_STATUSES = ['eligible', 'partial', 'depreciation', 'ineligible'] as const
export type VatStatus = (typeof VAT_STATUSES)[number]

export type PayStatus = 'unpaid' | 'partial' | 'paid'

export const EDOC_TYPES = ['e_invoice', 'e_archive'] as const
export type EDocType = (typeof EDOC_TYPES)[number]
export const EDOC_STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'cancelled'] as const
export type EDocStatus = (typeof EDOC_STATUSES)[number]

export const CARD_BRANDS = ['visa', 'mastercard', 'troy', 'amex'] as const
export type CardBrand = (typeof CARD_BRANDS)[number]

/**
 * Kasa / banka / POS / kredi kartı. Tam IBAN listede dönmez (yalnız maskeli); "göster" ayrı uçtan alınır ve
 * denetim kaydına yazılır. Tam kart numarası ve CVV hiçbir şekilde saklanmaz (PCI DSS) — yalnız son 4 hane.
 */
export type FinAccount = {
  id: string
  name: string
  type: AccountType
  bankName: string
  /** Merkezi banka listesindeki banka ve logosu. */
  bankId: string
  bankLogoUrl: string
  /** Hesap birimi; döviz/altında son alış kuruyla TL karşılığı. */
  currency: string
  balanceTry: string
  fxRate: string
  fxRateDate: string
  branch: string
  accountNo: string
  ibanMasked: string
  cardBrand: CardBrand | ''
  cardHolder: string
  cardLast4: string
  /** "AA/YY" */
  cardExpiry: string
  cardLimit: Money
  /** Banka hesabının nakit avans (KMH) limiti; 0 ise nakit avans kullanılamaz. */
  cashAdvanceLimit: Money
  statementDay: number | null
  dueDay: number | null
  merchantId: string
  openingBalance: Money
  active: boolean
  /** Sunucu hesaplar. */
  balance: Money
  /** Yalnız kredi kartı: limit − borç. */
  availableLimit: Money | null
  /**
   * Yalnız sanal POS: tahsilatın kasa/bankaya aktarımı. manual: oran aktarımda girilir; auto: valör günü sonra
   * kayıtlı oranla hedef hesaba otomatik aktarılır. Komisyon "Banka ve POS Komisyonları" gideri olur.
   */
  settlementMode: 'manual' | 'auto'
  settlementDays: number | null
  commissionRate: string
  settlementAccountId: string
}

export type Category = { id: string; name: string; kind: TxKind; vatStatus: VatStatus; color: number; active: boolean }

export type Counterparty = { id: string; name: string; kind: 'supplier' | 'customer'; taxNo: string; taxOffice: string; bankName: string; ibanMasked: string; email: string; phone: string }

export type Payment = { id: string; transactionId: string; accountId: string; date: string; amount: Money; channel: PaymentChannel; reference: string; note: string; hasReceipt: boolean }

export type Transaction = {
  id: string
  kind: TxKind
  counterpartyId: string
  categoryId: string
  docNo: string
  date: string
  dueDate: string
  description: string
  net: Money
  vatRate: number
  withholdingRate: number
  hasDocument: boolean
  review: ReviewStatus
  accountantNote: string
  /** Gider departmanı (boş: kategorinin varsayılanı ya da departmansız). */
  departmentId: string
  /** Sunucu hesaplar. */
  vat: Money
  withholding: Money
  total: Money
  paid: Money
  remaining: Money
  status: PayStatus
}

export type Transfer = { id: string; fromId: string; toId: string; date: string; amount: Money; note: string }

/** Faturaya bağlı olmayan doğrudan kasa/hesap girişi ya da çıkışı (peşin satış, küçük harcama, sermaye girişi vb.). */
export type CashEntry = {
  id: string
  accountId: string
  direction: 'in' | 'out'
  date: string
  amount: Money
  description: string
  categoryId: string | null
  reference: string
  /** İsteğe bağlı gider departmanı (boşsa kategorinin varsayılanı). */
  departmentId?: string
}

export type AccountMovement = {
  id: string
  date: string
  accountId: string
  description: string
  amount: Money
  direction: 'in' | 'out'
  source: 'payment' | 'transfer' | 'entry'
  refId: string
  createdBy: string
  createdAt: string
  /** İptal edilen hareket listede kalır, bakiyeye ve dönem toplamlarına girmez. */
  voided: boolean
  voidedAt: string
  voidedBy: string
  voidRequestedBy: string
  voidReason: string
  voidDecisionNote: string
}

/** Seçilen dönem için hesap özeti (sunucu hesaplar). */
export type AccountPeriodSummary = { accountId: string; opening: Money; inflow: Money; outflow: Money; closing: Money; count: number }

export type EDocument = {
  id: string
  type: EDocType
  direction: 'outgoing' | 'incoming'
  number: string
  ettn: string
  counterpartyId: string
  transactionId: string | null
  date: string
  total: Money
  status: EDocStatus
  statusNote: string
}

export type TaxParams = { vatRates: number[]; withholdingRate: number; corporateRate: number; depreciationYears: number; updatedAt: string }

export type TaxCalendarItem = { id: string; date: string; title: string; kind: 'vat' | 'withholding' | 'corporate' | 'ledger' | 'ba_bs'; done: boolean }

/* ---- Sunucu raporları ---- */

export type Trend = { value: Money; changePct: number | null }

export type Overview = {
  period: string
  collected: Trend
  paidOut: Trend
  netCash: Trend
  receivable: { value: Money; parties: number; docs: number }
  payable: { value: Money; parties: number; docs: number }
  monthly: { month: string; income: Money; expense: Money }[]
  categories: { categoryId: string; amount: Money; pct: number }[]
  expenseTotal: Money
  accrual: { income: Trend; expense: Trend; result: Trend }
  tax: { vat: Money; withholding: Money; corporate: Money }
  missingDocs: number
  categoryDetail: { categoryId: string; amount: Money; documented: Money; missing: Money; count: number; missingCount: number; review: ReviewStatus | 'mixed' }[]
}

export type CashFlow = {
  months: { month: string; inflow: Money; outflow: Money; net: Money; closing: Money }[]
  upcoming: { transactionId: string; kind: TxKind; dueDate: string; remaining: Money; overdue: boolean }[]
  projected: { inflow: Money; outflow: Money; closing: Money }
  cash: Money
}

export type TaxReport = {
  period: string
  vat: { output: Trend; input: Trend; carried: Money; payable: Money; reconciliationPct: number }
  monthly: { month: string; output: Money; input: Money; payable: Money }[]
  review: { approved: number; pending: number; problem: number }
  withholding: { total: Money; rows: { transactionId: string; base: Money; amount: Money }[] }
  /** Yıl başından döneme kurumlar vergisi tahmini (yevmiyeden); faiz geliri matraha dahil, peşin ödenen stopaj mahsup. */
  corporate: { base: Money; estimate: Money; ytdIncome: Money; ytdExpense: Money; interestIncome: Money; gross: Money; prepaidTax: Money; rate: string }
  docCheck: { missing: number; missingAmount: Money; unreviewed: number }
  vatRows: { transactionId: string; vatStatus: VatStatus }[]
}

export type AccountInput = Pick<
  FinAccount,
  'name' | 'type' | 'bankName' | 'bankId' | 'currency' | 'branch' | 'accountNo' | 'cardBrand' | 'cardHolder' | 'cardLast4' | 'cardExpiry' | 'cardLimit' | 'cashAdvanceLimit' | 'statementDay' | 'dueDay' | 'merchantId' | 'openingBalance' | 'active'
  | 'settlementMode' | 'settlementDays' | 'commissionRate' | 'settlementAccountId'
> & {
  /** Boş bırakılırsa mevcut IBAN korunur; `clearIban` ile silinir. */
  iban: string
  clearIban?: boolean
}
export type CategoryInput = Omit<Category, 'id'>
export type CounterpartyInput = Omit<Counterparty, 'id' | 'ibanMasked'> & { iban: string; clearIban?: boolean }
export type TransactionInput = Pick<
  Transaction,
  'kind' | 'counterpartyId' | 'categoryId' | 'docNo' | 'date' | 'dueDate' | 'description' | 'net' | 'vatRate' | 'withholdingRate' | 'hasDocument' | 'review' | 'accountantNote' | 'departmentId'
>
export type PaymentInput = Omit<Payment, 'id'>
export type TransferInput = Omit<Transfer, 'id'> & {
  /** POS'tan kasa/bankaya aktarımda o anki komisyon oranı (yüzde); diğer aktarımlarda gönderilmez. */
  commissionRate?: string
  /** Farklı birimli hesaplar arası aktarımda hedef hesaba geçen tutar (hedef biriminde). */
  toAmount?: string
}
export type CashEntryInput = Omit<CashEntry, 'id'> & {
  /** Döviz/altın hesabında bankanın uyguladığı kur; boşsa TCMB alış kuru. */
  fxRate?: string
}
export type EDocInput = Pick<EDocument, 'type' | 'transactionId'>

export type SensitiveAudit = { id: string; at: string; by: string; target: 'account' | 'party'; targetId: string; field: 'iban' }

/* ---- Ödeme planı (kira, SGK, vergi, abonelik… ve düzenli gelirler) ---- */

export const OBLIGATION_KINDS = ['rent', 'sgk', 'tax', 'salary', 'utility', 'subscription', 'service', 'loan', 'insurance', 'other'] as const
export type ObligationKind = (typeof OBLIGATION_KINDS)[number]
export const RECURRENCES = ['monthly', 'quarterly', 'yearly', 'none'] as const
export type Recurrence = (typeof RECURRENCES)[number]
export const REMIND_DAY_OPTIONS = [0, 1, 3, 7, 14, 30] as const
export type ObligationDirection = 'out' | 'in'

export type ObligationRate = { id: string; validFrom: string; amount: Money; note: string }

/** Tanım: tutar dönemsel oranlardan gelir (ör. kira artışı yeni oranla girilir, bekleyen vadeler güncellenir). */
export type Obligation = {
  id: string
  title: string
  kind: ObligationKind
  direction: ObligationDirection
  categoryId: string
  partyId: string
  accountId: string
  recurrence: Recurrence
  dueDay: number
  startDate: string
  endDate: string
  periodOffset: number
  vatRate: number
  withholdingRate: number
  remindDays: number[]
  notes: string
  source: 'manual' | 'payroll'
  active: boolean
  currentAmount: Money
  nextDue: string
  rates: ObligationRate[]
  /** Kalemlerden doğan gider kayıtlarının departmanı (boşsa kategori varsayılanı). */
  departmentId: string
}

export type ObligationInput = {
  title: string
  kind: ObligationKind
  direction: ObligationDirection
  categoryId: string
  partyId: string
  accountId: string
  recurrence: Recurrence
  dueDay: number
  startDate: string
  endDate: string
  periodOffset: number
  vatRate: number
  withholdingRate: number
  remindDays: number[]
  notes: string
  active: boolean
  departmentId: string
  /** Yalnız yeni tanımda: başlangıç tutarı. Sonraki değişiklikler oran geçmişine eklenir. */
  amount?: Money
}

export type ObligationItemStatus = 'pending' | 'paid' | 'skipped'
export type ObligationItem = {
  id: string
  obligationId: string
  title: string
  kind: ObligationKind
  direction: ObligationDirection
  period: string
  dueDate: string
  amount: Money
  locked: boolean
  status: ObligationItemStatus
  overdue: boolean
  daysLeft: number
  note: string
  accountId: string
  ledgerEntryId: string
  paidOn: string
}

export type ObligationSummary = {
  overdueCount: number
  overdueAmount: Money
  monthOut: Money
  monthIn: Money
  monthPaid: Money
  next30Out: Money
  next30In: Money
  next7Count: number
}

export type ObligationPayInput = { accountId: string; paidOn: string; amount: Money; channel: PaymentChannel; docNo: string; note: string }
